import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

export interface AdPlacement {
  id: string;
  name: string;
  slug: string;
  location: string;
  description: string | null;
  max_slots: number;
  price_monthly: number;
}

export interface AdBooking {
  id: string;
  placement_id: string;
  tool_id: string;
  status: string;
  start_date: string;
  end_date: string;
  tools?: { 
    id: string; 
    name: string; 
    slug: string; 
    logo_url: string | null; 
    short_description: string;
    is_verified?: boolean;
    verification_tier?: string;
  };
}

export function useAdPlacements() {
  return useQuery({
    queryKey: ['ad-placements'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('ad_placements')
        .select('*')
        .eq('is_active', true);
      
      if (error) throw error;
      return data as AdPlacement[];
    },
  });
}

export function useActiveAdsForPlacement(placementSlug: string) {
  return useQuery({
    queryKey: ['active-ads', placementSlug],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      
      const { data: placement } = await (supabase as any)
        .from('ad_placements')
        .select('id')
        .eq('slug', placementSlug)
        .single();
      
      if (!placement) return [];
      
      const { data, error } = await (supabase as any)
        .from('ad_bookings')
        .select('*, tools:tool_id (id, name, slug, logo_url, short_description, is_verified, verification_tier)')
        .eq('placement_id', placement.id)
        .eq('status', 'active')
        .lte('start_date', today)
        .gte('end_date', today);
      
      if (error) throw error;
      return data as AdBooking[];
    },
    enabled: !!placementSlug,
  });
}

export function useBookAdPlacement() {
  return useMutation({
    mutationFn: async ({ placementId, toolId, months }: { placementId: string; toolId: string; months: number }) => {
      const { data, error } = await supabase.functions.invoke('ad-booking-checkout', {
        body: { placementId, toolId, months },
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.open(data.url, '_blank');
        toast({ title: 'Redirecting to checkout', description: 'Complete your ad booking in the new tab.' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Booking failed', description: error.message, variant: 'destructive' });
    },
  });
}
